import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAccountAsset,
  deleteAccountAsset,
  listAccountAssets,
  updateAccountAsset,
} from "../../api/endpoints/accountAssets.js";
import { listAccountAssetTypes } from "../../api/endpoints/accountAssetTypes.js";
import { buildAssetListSubtitle, deriveAssetDisplayName } from "../../pages/Accounts/assetUtils.js";

export function useAccountAssets(accountId) {
  const [items, setItems] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const assetTypeMap = useMemo(
    () => Object.fromEntries(assetTypes.map((type) => [type.id, type])),
    [assetTypes],
  );

  const reload = useCallback(async () => {
    if (!accountId) {
      setItems([]);
      setAssetTypes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [assets, types] = await Promise.all([
        listAccountAssets(accountId),
        listAccountAssetTypes(accountId),
      ]);
      setItems(assets);
      setAssetTypes(types);
      setSelectedId((prev) => {
        if (prev && assets.some((row) => row.id === prev)) return prev;
        return assets[0]?.id ?? null;
      });
    } catch (err) {
      setError(err.message ?? "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((asset) => {
      const typeName = assetTypeMap[asset.assetTypeId]?.name ?? "";
      const valueText = Object.values(asset.values ?? {}).join(" ");
      const displayName = deriveAssetDisplayName(asset, assetTypeMap[asset.assetTypeId]);
      return [displayName, typeName, valueText].some((part) => String(part).toLowerCase().includes(q));
    });
  }, [items, search, assetTypeMap]);

  useEffect(() => {
    if (visible.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!visible.some((row) => row.id === selectedId)) {
      setSelectedId(visible[0].id);
    }
  }, [visible, selectedId]);

  const selected = useMemo(
    () => items.find((row) => row.id === selectedId) ?? null,
    [items, selectedId],
  );

  const selectedAssetType = selected ? assetTypeMap[selected.assetTypeId] ?? null : null;

  const addAsset = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const created = await createAccountAsset(accountId, payload);
      setItems((list) => [created, ...list]);
      setSelectedId(created.id);
      return created;
    } catch (err) {
      setError(err.message ?? "Failed to create asset");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const saveAsset = useCallback(async (id, payload) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateAccountAsset(accountId, id, payload);
      setItems((list) => list.map((row) => (row.id === id ? updated : row)));
      return updated;
    } catch (err) {
      setError(err.message ?? "Failed to save asset");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const removeAsset = useCallback(async (id) => {
    setSaving(true);
    setError(null);
    try {
      await deleteAccountAsset(accountId, id);
      setItems((list) => list.filter((row) => row.id !== id));
    } catch (err) {
      setError(err.message ?? "Failed to delete asset");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const getAssetTypeName = useCallback(
    (assetTypeId) => assetTypeMap[assetTypeId]?.name ?? "—",
    [assetTypeMap],
  );

  const getAssetSubtitle = useCallback(
    (asset) => buildAssetListSubtitle(asset, assetTypeMap[asset.assetTypeId]),
    [assetTypeMap],
  );

  return {
    items: visible,
    allItems: items,
    assetTypes,
    assetTypeMap,
    loading,
    saving,
    error,
    search,
    setSearch,
    selected,
    selectedAssetType,
    selectedId,
    setSelectedId,
    addAsset,
    saveAsset,
    removeAsset,
    reload,
    getAssetTypeName,
    getAssetSubtitle,
    assetTypeMap,
  };
}
